import { useEffect, useState, type FormEvent } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { FormErrorAlert } from '@/features/auth/FormErrorAlert';
import { getErrorMessage } from '@/lib/api-error';
import { EMPTY_FORM_ERRORS, fieldError, formErrorsFromApiError } from '@/lib/form-errors';
import type { Campaign } from '@/types/campaigns';
import {
  CAMPAIGN_FORM_FIELDS,
  campaignFormStateFrom,
  createCampaignPayload,
  emptyCampaignForm,
  monedaOptions,
  updateCampaignPayload,
  type CampaignFormState,
} from './campaigns-payload';
import { CAMPAIGN_LIMITS, validateCampaignForm } from './campaigns-validation';
import { useCreateCampaign, useUpdateCampaign } from './use-campaigns';

/**
 * Alta y edición de una fila de gasto.
 *
 * El formulario tiene la forma del contrato, no la de la tabla: primero el ALCANCE (¿el gasto es de
 * un anuncio o de la campaña entera?) porque decide qué ids hacen falta —con `metaAdId` el id de la
 * campaña es opcional y solo agrupa; sin él, el de la campaña es obligatorio—, y después el nombre,
 * el gasto acumulado y la moneda.
 *
 * El `409` del backend (ese anuncio o esa campaña ya tienen fila) no es un error de un campo: cae en
 * el aviso general del formulario con el texto exacto que devolvió la API, que ya dice qué fila
 * existe y qué hacer («editá esa fila en vez de crear otra»).
 */

export interface CampaignDialogProps {
  open: boolean;
  onClose: () => void;
  tenantId: string | null;
  /** Fila que se edita. `null` = alta. */
  campaign: Campaign | null;
}

export function CampaignDialog({ open, onClose, tenantId, campaign }: CampaignDialogProps) {
  const createCampaign = useCreateCampaign(tenantId);
  const updateCampaign = useUpdateCampaign(tenantId);

  const [form, setForm] = useState<CampaignFormState>(emptyCampaignForm);
  const [errors, setErrors] = useState(EMPTY_FORM_ERRORS);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /** Cada apertura arranca limpia (o con los datos de la fila que se va a editar). */
  useEffect(() => {
    if (!open) return;

    setForm(campaignFormStateFrom(campaign));
    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);
  }, [open, campaign]);

  const saving = createCampaign.isPending || updateCampaign.isPending;
  const isEditing = campaign !== null;
  const porAnuncio = form.alcance === 'anuncio';

  /** Cambia un campo sin tocar los demás. */
  function patch(changes: Partial<CampaignFormState>) {
    setForm((current) => ({ ...current, ...changes }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const nextErrors = validateCampaignForm(form);
    if (Object.keys(nextErrors.fieldErrors).length > 0) {
      setErrors(nextErrors);
      setSubmitError(null);
      return;
    }

    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);

    try {
      if (isEditing && campaign) {
        await updateCampaign.mutateAsync({
          campaignId: campaign.id,
          payload: updateCampaignPayload(form),
        });
      } else {
        await createCampaign.mutateAsync(createCampaignPayload(form));
      }

      onClose();
    } catch (error) {
      // El backend valida lo mismo (y más): sus mensajes se reparten entre el campo que los produjo
      // y el aviso general del formulario (`409` de duplicado, `400` del `refine`).
      setErrors(formErrorsFromApiError(error, CAMPAIGN_FORM_FIELDS));
      setSubmitError(getErrorMessage(error));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={isEditing ? 'Editar la fila de gasto' : 'Nueva fila de gasto'}
      description="Esta tabla es la única fuente de gasto del CPA: lo que no esté acá, los reportes no lo pueden contar."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          {/* `form` enlaza este botón del pie con el formulario del cuerpo del modal. */}
          <Button type="submit" form="campaign-form" loading={saving}>
            {isEditing ? 'Guardar cambios' : 'Crear la fila'}
          </Button>
        </>
      }
    >
      <form id="campaign-form" onSubmit={handleSubmit} noValidate className="space-y-5">
        <FormErrorAlert message={submitError} errors={errors} />

        <Field
          label="Alcance del gasto"
          htmlFor="campaignAlcance"
          required
          hint="Con el anuncio, el id de la campaña es opcional (solo agrupa). Sin anuncio, el gasto es de la campaña entera."
        >
          <Select
            id="campaignAlcance"
            name="alcance"
            value={form.alcance}
            disabled={saving}
            onChange={(event) =>
              patch({ alcance: event.target.value as CampaignFormState['alcance'] })
            }
          >
            <option value="anuncio">Un anuncio concreto (gasto del ad_id)</option>
            <option value="campana">La campaña entera (sin desglose por anuncio)</option>
          </Select>
        </Field>

        <Field
          label="Nombre"
          htmlFor="campaignNombre"
          required
          hint="Como lo reconoces tú al revisar el gasto (ej. «Anuncio blusas · julio»)."
          error={fieldError(errors, 'nombre')}
        >
          <Input
            id="campaignNombre"
            name="nombre"
            placeholder="Ej. Anuncio blusas de lino"
            autoFocus
            maxLength={CAMPAIGN_LIMITS.nombre}
            value={form.nombre}
            invalid={Boolean(fieldError(errors, 'nombre'))}
            onChange={(event) => patch({ nombre: event.target.value })}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          {porAnuncio ? (
            <Field
              label="Id del anuncio (Meta)"
              htmlFor="campaignMetaAdId"
              required
              hint="El `ad_id` que llega en el referral del webhook."
              error={fieldError(errors, 'metaAdId')}
            >
              <Input
                id="campaignMetaAdId"
                name="metaAdId"
                placeholder="Ej. 120210123456789"
                maxLength={CAMPAIGN_LIMITS.metaId}
                value={form.metaAdId}
                invalid={Boolean(fieldError(errors, 'metaAdId'))}
                onChange={(event) => patch({ metaAdId: event.target.value })}
              />
            </Field>
          ) : null}

          <Field
            label={porAnuncio ? 'Id de la campaña (opcional)' : 'Id de la campaña (Meta)'}
            htmlFor="campaignMetaCampaignId"
            required={!porAnuncio}
            hint={
              porAnuncio
                ? 'Si lo sabes, agrupa esta fila con las de los otros anuncios de la misma campaña.'
                : 'El `campaign_id` del referral; es lo único que identifica la fila.'
            }
            error={fieldError(errors, 'metaCampaignId')}
          >
            <Input
              id="campaignMetaCampaignId"
              name="metaCampaignId"
              placeholder="Ej. 120210987654321"
              maxLength={CAMPAIGN_LIMITS.metaId}
              value={form.metaCampaignId}
              invalid={Boolean(fieldError(errors, 'metaCampaignId'))}
              onChange={(event) => patch({ metaCampaignId: event.target.value })}
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Gasto acumulado"
            htmlFor="campaignGastoActual"
            hint="Lo gastado en el periodo (no el gasto de un día). Déjalo vacío al crear: arranca en 0."
            error={fieldError(errors, 'gastoActual')}
          >
            <Input
              id="campaignGastoActual"
              name="gastoActual"
              inputMode="decimal"
              placeholder="0"
              value={form.gastoActual}
              invalid={Boolean(fieldError(errors, 'gastoActual'))}
              onChange={(event) => patch({ gastoActual: event.target.value })}
            />
          </Field>

          <Field
            label="Moneda"
            htmlFor="campaignMoneda"
            hint="Código ISO de 3 letras."
            error={fieldError(errors, 'moneda')}
          >
            <Select
              id="campaignMoneda"
              name="moneda"
              value={form.moneda}
              invalid={Boolean(fieldError(errors, 'moneda'))}
              disabled={saving}
              onChange={(event) => patch({ moneda: event.target.value })}
            >
              {monedaOptions(form.moneda).map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="flex items-center justify-between gap-3 rounded-panel border border-line px-3.5 py-3">
          <div className="min-w-0">
            <p className="text-caption font-medium text-ink-soft">
              Contar este gasto en los reportes
            </p>
            <p className="text-micro text-ink-faint">
              Las filas inactivas no cuentan para el CPA: úsalas cuando el anuncio ya terminó.
            </p>
          </div>

          <span className="flex shrink-0 items-center gap-2.5">
            <Switch
              id="campaignActivo"
              checked={form.activo}
              label="Contar este gasto en los reportes"
              disabled={saving}
              onChange={(activo) => patch({ activo })}
            />
            <Badge tone={form.activo ? 'success' : 'neutral'}>
              {form.activo ? 'Cuenta' : 'No cuenta'}
            </Badge>
          </span>
        </div>
      </form>
    </Modal>
  );
}
